import { AxiosCall } from '../axios';
import { environment } from '../environment/environment';
import {
  ClinicMetricsData,
  ClinicsVaccineDetailsByMonthData,
  ReturnedVaccinesByPeriodParams,
  ReturnedVaccinesByPeriodData
} from './service-mappers/MetricsServiceMapper';
import { ServiceSuccessResponseType } from './service-mappers/BaseMapper';
import { ClinicBaseParams } from './service-mappers/CommonServiceMapper';
import { serializeQueryParams } from './PaymentHistoryService';
import { CancelTokenSource } from 'axios';

const getCardsCount = environment.apiEndPoints.metrics.cardsCount;
const getDetailsByMonth = environment.apiEndPoints.metrics.monthWiseVaccines;
const getReturnDetailsByMonth =
  environment.apiEndPoints.metrics.returnedVaccines;

export const getCardsCountForClinic = async (
  params: ClinicBaseParams
): Promise<ServiceSuccessResponseType<ClinicMetricsData>> => {
  return AxiosCall.get(`/clinic/${params.clinicId}${getCardsCount}`);
};

export const getVaccineDetailsByMonth = async (
  params: ClinicBaseParams
): Promise<
  ServiceSuccessResponseType<Array<ClinicsVaccineDetailsByMonthData>>
> => {
  return AxiosCall.get(`/clinic/${params.clinicId}${getDetailsByMonth}`);
};

export const getReturnedMetricsByPeriod = async (
  params: ReturnedVaccinesByPeriodParams
): Promise<ServiceSuccessResponseType<Array<ReturnedVaccinesByPeriodData>>> => {
  const urlParams = `${params.startDate ? `&start_date=${params.startDate}` : ''
    }
    ${params.endDate ? `&end_date=${params.endDate}` : ''}`.trim();

  return AxiosCall.get(
    `/clinic/${params.clinicId}${getReturnDetailsByMonth}${urlParams}`
  );
};
export const getDashBoardCounts = async (): Promise<
  ServiceSuccessResponseType<any>
> => {
  return AxiosCall.get(`/metrics/dashboard-cards`);
};
// Added canceltoken to cancel metrics message api, if the metrics message api calls are made in quick succession
export const getUserDashboardMessagesCardData = async (
  params: ClinicBaseParams,
  cancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<any>> => {
  return AxiosCall.get(
    `/clinic-group/${params.groupId}/metrics/message`,
    cancelToken
  );
};
// Added canceltoken to cancel metrics payment api, if the metrics payment api calls are made in quick succession
export const getUserDashboardPaymentsCardData = async (
  params: {
    groupId: string | number;
    startDate: string;
    endDate: string;
  },
  cancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<any>> => {
  let queryParams = '';
  if (params.startDate) {
    queryParams += `start_date=${params.startDate}`;
  }
  if (params.endDate) {
    queryParams += `${queryParams ? '&' : ''}end_date=${params.endDate}`;
  }
  return AxiosCall.get(
    `/clinic-group/${params.groupId}/metrics/payment/?${queryParams}`,
    cancelToken
  );
};
// Added canceltoken to cancel metrics statement api, if the metrics statement api calls are made in quick succession
export const getUserDashboardStatementsCardData = async (
  params: ClinicBaseParams,
  cancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<any>> => {
  return AxiosCall.get(
    `/clinic-group/${params.groupId}/metrics/statement`,
    cancelToken
  );
};

export const getUserDashboardCCMCardData = async (
  params: ClinicBaseParams,
  options?: {
    clinic_id?: number;
  },
  cancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<any>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${params.groupId}/metrics/ccm?${queryParams}`,
    cancelToken
  );
};
export const getCCMUserData = async (
  group_id: number,
  options?: {
    clinic_id?: number;
  },
  cancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<any>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${group_id}/ccm/user-stats?${queryParams}`,
    cancelToken
  );
};

export const getStaffPerformance = async (
  group_id: number,
  options?: {
    clinic_id?: number;
    search?: string;
    start_date?: string;
    end_date?: string;
  },
): Promise<ServiceSuccessResponseType<any>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${group_id}/ccm/staff-performance?${queryParams}`,
  );
};

export const getUserDashboardCCMUpcomingAppointmentsDetailCard = async (
  clinic_group_id: number,
  options?: {
    limit?: number;
    offset?: number;
  },
  cancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<any>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/ccm/patient/upcoming-appointment-patients?${queryParams}`,
    cancelToken
  );
};

// ** Prior Authorizations and Claims Module Unattended Tickets **//
export const getUnattendedTickets = async (
  clinic_group_id: number | string,
  options: {
    module: any;
  },
  cancelToken?: CancelTokenSource
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `clinic-group/${clinic_group_id}/metrics/tickets?${queryParams}`,
    cancelToken
  );
};

export const getUserDashboardCCMUpcomingFollowUpNotesDetailCard = async (
  clinic_group_id: number,
  options?: {
    limit?: number;
    offset?: number;
  },
  cancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<any>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/ccm-patient/pending-followups?${queryParams}`,
    cancelToken
  );
};

export const getStaffPerformanceDetails = async (
  clinic_group_id: any,
  staff_performace_id: any,
  options?: {
    clinic_id?: any;
    start_date?: any;
    end_date?: any;
  }
): Promise<ServiceSuccessResponseType<any>> => {
  // Build query params object conditionally
  const queryObj: { [key: string]: any } = {};

  if (options) {
    if (options.clinic_id) {
      queryObj.clinic_id = options.clinic_id;
    }
    if (options.start_date) {
      queryObj.start_date = options.start_date;
    }
    if (options.end_date) {
      queryObj.end_date = options.end_date;
    }
  }

  const queryParams = serializeQueryParams(queryObj);

  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/ccm/staff-performance/${staff_performace_id}?${queryParams}`
  );
};
